import { useCallback, useRef, useState } from 'react';
import {
  beslisVervolg,
  bouwDoorvraagVoorInsturen,
  MAX_HERBEOORDELINGEN,
  type Beoordeling,
  type DoorvraagVraag,
  type MeldingDoorvraag,
  type Vervolg,
} from '../lib/meldingStatus';
import { meldingenService, type DuidelijkheidInvoer } from '../services/meldingenService';

export interface DuidelijkheidStaat {
  /** Laatste uitkomst van de AI (null tot de eerste check). */
  beoordeling: Beoordeling | null;
  /** De vragen van de laatste ronde, mét de antwoorden die de melder al koos. */
  vragen: DoorvraagVraag[];
  /** Aantal herbeoordelingen tot nu toe (0 = alleen de eerste check). */
  ronde: number;
  bezig: boolean;
  /** Nog een herbeoordeling mogelijk na antwoorden? */
  kanOpnieuw: boolean;
}

/**
 * De doorvraag-cyclus van de meldknop: eerste check → (vragen → antwoorden →
 * herbeoordeling) × max 2 → insturen als 'nieuw' of 'onvolledig'. De hook
 * bewaart alle gestelde vragen met antwoorden, zodat ze bij het insturen in
 * de kolom doorvraag belanden.
 */
export function useMeldingDuidelijkheid() {
  const [beoordeling, setBeoordeling] = useState<Beoordeling | null>(null);
  const [vragen, setVragen] = useState<DoorvraagVraag[]>([]);
  const [ronde, setRonde] = useState(0);
  const [bezig, setBezig] = useState(false);
  const sessieId = useRef<string | null>(null);
  // Vragen uit eerdere rondes (met antwoorden), voor de AI én de kolom.
  const geschiedenis = useRef<DoorvraagVraag[]>([]);
  // Hoeveel beoordelingen er al gedaan zijn in dit formulier.
  const beoordelingen = useRef(0);

  const reset = useCallback(() => {
    setBeoordeling(null);
    setVragen([]);
    setRonde(0);
    setBezig(false);
    sessieId.current = null;
    geschiedenis.current = [];
    beoordelingen.current = 0;
  }, []);

  const zetAntwoord = useCallback((index: number, antwoord: string) => {
    setVragen((prev) => prev.map((v, i) => (i === index ? { ...v, antwoord } : v)));
  }, []);

  /**
   * Laat de AI (opnieuw) beoordelen. De tweede en volgende keer gaan de
   * vragen van de vorige ronde mét antwoorden mee. Geeft terug wat er nu
   * moet gebeuren: doorvragen (het paneel toont de vragen) of insturen.
   */
  const beoordeel = useCallback(
    async (
      basis: Omit<
        DuidelijkheidInvoer,
        'sessieId' | 'ronde' | 'eerdereVragen' | 'screenshotBase64'
      > & {
        screenshotBase64?: string;
      },
    ): Promise<Vervolg> => {
      const huidigeRonde = beoordelingen.current;
      if (huidigeRonde > MAX_HERBEOORDELINGEN) {
        return { actie: 'insturen', status: 'onvolledig' };
      }
      setBezig(true);
      try {
        const eerdereVragen = [...geschiedenis.current, ...vragen];
        const uitkomst = await meldingenService.beoordeelDuidelijkheid({
          ...basis,
          sessieId: sessieId.current,
          ronde: huidigeRonde,
          eerdereVragen,
          // De schermafbeelding alleen bij de eerste check (kosten).
          screenshotBase64: huidigeRonde === 0 ? basis.screenshotBase64 : undefined,
        });
        if (uitkomst.sessieId) sessieId.current = uitkomst.sessieId;
        beoordelingen.current = huidigeRonde + 1;
        geschiedenis.current = eerdereVragen;
        setBeoordeling(uitkomst);
        setRonde(huidigeRonde);
        const vervolg = beslisVervolg(uitkomst, huidigeRonde);
        setVragen(vervolg.actie === 'doorvragen' ? uitkomst.vragen : []);
        return vervolg;
      } finally {
        setBezig(false);
      }
    },
    [vragen],
  );

  /** Alles wat in de kolom doorvraag hoort bij het insturen. */
  const doorvraagVoorInsturen = useCallback(
    (status: 'nieuw' | 'onvolledig'): MeldingDoorvraag | null =>
      bouwDoorvraagVoorInsturen({
        sessieId: sessieId.current,
        vragen: [...geschiedenis.current, ...vragen],
        beoordeling,
        rondes: ronde,
        status,
      }),
    [beoordeling, ronde, vragen],
  );

  const staat: DuidelijkheidStaat = {
    beoordeling,
    vragen,
    ronde,
    bezig,
    kanOpnieuw: beoordelingen.current <= MAX_HERBEOORDELINGEN,
  };

  return { staat, beoordeel, zetAntwoord, doorvraagVoorInsturen, reset };
}
